import React, { useState } from "react";
import { useChatContext } from "@/hooks/useChatContext";
import { ChatMessages } from "@/components/agent/chat/ChatMessages";
import { ChatInput } from "@/components/agent/chat/ChatInput";
import { ResetButton } from "@/components/agent/chat/ResetButton";
import { ConversationStarters } from "@/components/agent/chat/ConversationStarters";
import { FollowUpSuggestions } from "@/components/agent/chat/FollowUpSuggestions";
import {
  parseRichContent,
  filterRichContentByCapabilities,
} from "@/utils/richContentParser";
import { ChatContextType } from "@/hooks/useChatContext";
import { useSearchParams } from "react-router-dom";
import { sanitizeBrandColor } from "@/lib/utils";

interface DesktopChatContainerProps {
  constrainWidth?: boolean;
}

export const DesktopChatContainer: React.FC<DesktopChatContainerProps> = ({
  constrainWidth = false,
}) => {
  const {
    agent,
    organization,
    isPublic,
    chatMessages,
    isLoading,
    isConversationLoading,
    canResetConversation,
    handleSendMessage,
    handleResetConversation,
    handleCloseConversation,
    brandColor,
    textColor,
    conversationId,
  } = useChatContext();

  // State for message pagination
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Get hasMoreMessages and loadMoreMessages from the context
  const context = useChatContext() as ChatContextType & {
    hasMoreMessages?: boolean;
    loadMoreMessages?: () => Promise<void>;
  };

  const hasMoreMessages = context.hasMoreMessages || false;
  const loadMoreMessagesFromContext = context.loadMoreMessages;

  const handleLoadMoreMessages = async () => {
    if (loadMoreMessagesFromContext && !isLoadingMore) {
      setIsLoadingMore(true);
      try {
        await loadMoreMessagesFromContext();
      } catch (error) {
        console.error("Error loading more messages:", error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  };

  const showConversationStarters =
    chatMessages.length === 1 && agent?.conversation_starters?.length > 0;

  const lastAgentMessage = [...chatMessages]
    .reverse()
    .find((m) => m.type === "agent");

  // Parse rich content and filter based on agent capabilities
  const parsedContent = lastAgentMessage
    ? parseRichContent(lastAgentMessage.content)
    : {
        cleanContent: "",
        insideContent: [],
        bottomContent: [],
        belowContent: [],
      };
  const richContent = filterRichContentByCapabilities(parsedContent, agent);

  // Check if there's a scheduling widget
  const hasSchedulingWidget = richContent.belowContent?.some(
    (item) => item.type === "SCHEDULING" || item.type === "CALENDLY",
  );

  // Add "I've booked a slot" suggestion only if there's a scheduling widget
  const followUpSuggestions = [
    ...richContent.bottomContent
      .filter((item) => item.type === "FOLLOWUP")
      .map((item) => item.content),
    ...(hasSchedulingWidget ? ["I've booked a slot"] : []),
  ];

  const showFollowUps =
    followUpSuggestions.length > 0 && !showConversationStarters;

  // Check for embed mode
  const [searchParams] = useSearchParams();
  const isEmbedded =
    searchParams.get("embed") === "true" ||
    searchParams.get("embedBubble") === "true";

  // Desktop-optimized background styling
  const containerStyle: React.CSSProperties = {
    background: `linear-gradient(to bottom, ${sanitizeBrandColor(organization?.brand_color || "#3b82f6")}26 0%, #ffffff 100%)`,
    height: isEmbedded ? "100%" : "100dvh",
    width: isEmbedded ? "100%" : "auto",
  };

  return (
    <div
      className="relative flex h-[100dvh] flex-col overflow-hidden bg-white"
      style={containerStyle}
    >
      {/* Content with desktop-optimized z-index */}
      <div className="relative z-[1] flex flex-auto min-h-0 flex-col overflow-hidden">
        {/* Messages area with desktop padding - top and bottom */}
        <div className="flex-auto min-h-0 overflow-y-auto bg-transparent py-8">
          <div
            className={constrainWidth ? "max-w-[770px] mx-auto px-8" : "px-8"}
          >
            {/* Desktop-specific: Banner section with splash-screen style padding and top margin from header */}
            {organization?.banner_url && (
              <div className="w-full mb-5 mt-6">
                <img
                  src={organization.banner_url}
                  alt="Organization Banner"
                  className="w-full rounded-[7px] h-auto object-cover max-h-[140px]"
                />
              </div>
            )}

            <ChatMessages
              chatMessages={chatMessages}
              isLoading={isLoading}
              isConversationLoading={isConversationLoading}
              agent={agent}
              organization={organization}
              brandColor={brandColor}
              textColor={textColor}
              isPublic={isPublic}
              isMobile={false}
              onSendMessage={handleSendMessage}
              onCloseConversation={handleCloseConversation}
              hasMoreMessages={hasMoreMessages && !!conversationId}
              onLoadMoreMessages={handleLoadMoreMessages}
              isLoadingMore={isLoadingMore}
            />
          </div>
        </div>

        {/* Fixed elements at the bottom - desktop-optimized with bottom padding */}
        <div className="relative z-[1] flex-none pb-8">
          {/* Conversation starters - desktop layout */}
          {showConversationStarters && (
            <div
              className={
                constrainWidth ? "max-w-[770px] mx-auto px-4 py-3" : "px-4 py-3"
              }
            >
              <div className="flex justify-end">
                <ConversationStarters
                  starters={agent.conversation_starters}
                  onStarterClick={handleSendMessage}
                  brandColor={brandColor}
                />
              </div>
            </div>
          )}

          {/* Follow-up suggestions - desktop layout */}
          {showFollowUps && (
            <div
              className={
                constrainWidth ? "max-w-[770px] mx-auto px-4 py-3" : "px-4 py-3"
              }
            >
              <div className="flex justify-end">
                <FollowUpSuggestions
                  suggestions={followUpSuggestions}
                  onSuggestionClick={handleSendMessage}
                  brandColor={brandColor}
                />
              </div>
            </div>
          )}

          {/* Input bar - desktop-optimized */}
          <div
            className={constrainWidth ? "max-w-[770px] mx-auto px-8" : "px-8"}
          >
            <ChatInput
              onSendMessage={handleSendMessage}
              isLoading={isLoading}
              brandColor={brandColor}
              isPublic={isPublic}
              isMobile={false}
              onReset={handleResetConversation}
              canReset={canResetConversation}
              isDisabled={false}
              placeholder="Type your message..."
            />
          </div>
        </div>

        {/* Desktop-specific: Floating reset button positioning */}
        {chatMessages.length > 1 && !isPublic && !canResetConversation && (
          <ResetButton
            onReset={handleResetConversation}
            isLoading={isLoading}
            canReset={true}
            brandColor={brandColor}
            isMobile={false}
          />
        )}

        {/* Powered by Hubflo - desktop layout (outside padding area) */}
        {isPublic && (
          <div className="relative z-[1] px-4 py-2 text-center bg-gray-100 border-t">
            <a
              href="https://hubflo.com/agents"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center text-gray-400 text-xs hover:underline transition-all"
            >
              <div className="w-4 h-4 rounded-full overflow-hidden mr-1.5 flex-shrink-0">
                <img
                  src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/logo%20squared%20hubflo.webp`}
                  alt="Hubflo Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              Powered by Hubflo
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
